import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

const STATE = {
  linked: ['Journal linked', 'bg-emerald-100 text-emerald-800'],
  unlinked_journal: ['Journal found (not linked on payment)', 'bg-amber-100 text-amber-800'],
  missing: ['Journal missing', 'bg-red-100 text-red-700'],
  possible_duplicate: ['Possible duplicate journals — correction needs approval', 'bg-orange-100 text-orange-800'],
  payment_reversed: ['Payment reversed', 'bg-stone-200 text-stone-700'],
  credit_no_journal_needed: ['On credit — no payment journal needed', 'bg-sky-100 text-sky-800'],
  credit_mode_has_journal: ['On credit but has a payment journal — review', 'bg-amber-100 text-amber-800'],
};

const JournalCard = ({ j }) => (
  <div className="rounded-lg border p-2 text-xs" data-testid={`sp-journal-${j.entry_number}`}>
    <div className="flex justify-between font-medium">
      <span>{j.entry_number} · {j.reference} · {j.date}</span>
      <span className={j.status === 'reversed' ? 'text-stone-500 line-through' : 'text-emerald-700'}>
        {j.status}{j.reversed_by ? ` by ${j.reversed_by}` : ''}
      </span>
    </div>
    <table className="w-full mt-1">
      <tbody>
        {j.lines.map((l, i) => (
          <tr key={i}><td>{l.account_code} {l.account_name}</td>
            <td className="text-right">{Number(l.debit || 0).toFixed(2)}</td>
            <td className="text-right">{Number(l.credit || 0).toFixed(2)}</td></tr>
        ))}
      </tbody>
    </table>
    <a className="text-info underline" href={`/accounting/journal-entries?search=${encodeURIComponent(j.entry_number)}`}
      target="_blank" rel="noreferrer" data-testid={`sp-open-journal-${j.entry_number}`}>Open journal details</a>
  </div>
);

export const SupplierPaymentJournalDialog = ({ payment, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const load = () => api.get(`/supplier-payments/${payment.id}/journals`).then((r) => setData(r.data))
    .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load journals'));
  useEffect(() => { if (payment) load(); }, [payment?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const postMissing = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/supplier-payments/${payment.id}/post-missing-journal`, { reason, confirm: true });
      setConfirming(false);
      toast.success(`Journal ${r.data.entry_number} posted`);
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Not posted'); } finally { setBusy(false); }
  };
  const [label, cls] = STATE[data?.state] || [data?.state || 'Loading…', 'bg-stone-100'];
  return (
    <Dialog open={!!payment} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="sp-journal-dialog">
        <DialogHeader><DialogTitle>Payment {data?.reference || ''} — accounting</DialogTitle></DialogHeader>
        {data && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-1 text-xs">
              <span>Date: {String(data.payment.date || '').slice(0, 10)}</span>
              <span>Amount: {Number(data.payment.amount || 0).toFixed(2)}</span>
              <span>Method: {data.payment.payment_mode || '—'}</span>
              <span>Bank account: {data.payment.bank_account_id || 'Main bank (default)'}</span>
            </div>
            <Badge className={cls} data-testid="sp-journal-state">{label}</Badge>
            {data.journals.map((j) => <JournalCard key={j.id} j={j} />)}
            {data.reversals.length > 0 && <div className="text-xs font-medium">Linked reversals</div>}
            {data.reversals.map((j) => <JournalCard key={j.id} j={j} />)}
            {data.state === 'missing' && data.journals.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs" data-testid="sp-missing-ambiguous">
                Only reversed/correction journals exist for this payment — this needs accountant review; nothing can be posted here.
              </div>
            )}
            {data.state === 'missing' && data.journals.length === 0 && (
              <div className="rounded-lg border border-red-200 p-2 space-y-2">
                <div className="text-xs">No journal exists. An admin/manager can post the single Accounts Payable / {data.payment.payment_mode} journal.</div>
                <Input value={reason} onChange={(e) => { setReason(e.target.value); setConfirming(false); }} placeholder="Reason (required)" data-testid="sp-missing-reason" />
                {!confirming ? (
                  <Button size="sm" disabled={busy || reason.trim().length < 5} onClick={() => setConfirming(true)} data-testid="sp-post-missing-journal">
                    Post missing journal
                  </Button>
                ) : (
                  <div className="space-y-1" data-testid="sp-post-confirm">
                    <div className="text-xs font-medium">Confirm: post Dr Accounts Payable / Cr {data.payment.payment_mode} {Number(data.payment.amount || 0).toFixed(2)} dated {String(data.payment.date || '').slice(0, 10)}? This is recorded in the audit log.</div>
                    <Button size="sm" disabled={busy} onClick={postMissing} data-testid="sp-post-confirm-btn">Yes, post once</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>Cancel</Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
